import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Checkbox,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Slide,
  FormControl,
  FormControlLabel,
  Button,
  Link,
  Backdrop,
  ShareIcon,
  AddIcon,
} from "../../../ui";
import {
  InfoOutlined as InfoIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AccountBalance as AccountBalanceIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  AccessTimeFilled as AccessTimeFilledIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useUI } from "@/shared/state/store";
import { SharePageService } from "@/shared/api/services/sharePageService";

import AdditionalSupportModal from "./AdditionalSupportModal";
import EditCompanyDialog from "@/components/EditCompanyDialog";
import DeleteCompanyDialog from "@/components/DeleteCompanyDialog";

import {
  ApiResponse,
  ApplianceData,
  CalculatedTotals,
  CustomerDeclarationEntry,
  CustomerLatestSnapshot,
  CustomerPreferencesEntry,
  CustomerProfileData,
  CustomerSharingResponse,
  DeclarationAllEntry,
  DigitalLender,
  NotifyCustomerDebtAdvice,
  NotifyCustomerDebtAdviceResponse,
  SendEmailOnShareEntry,
  ServiceResponse,
  SharedDocResponse,
} from "@/shared/api/types";
import { httpGet, httpPatch, httpPost, httpPut } from "@/shared/api/http";
import {
  customerEndpoints,
  debtEndpoints,
  declarationEndpoints,
  documentsPageEndpoints,
  latestUpdateEndpoints,
  onboardingEndpoints,
  sharePageEndpoints,
} from "@/shared/api/endpoints";
import femaCapture from "@/shared/fema/capture";
import AddCompanyDialog from "@/components/AddCompanyDialog";
import ShareHistoryDialog from "@/components/ShareHistoryDialog";
import UUDeclarationDialog from "@/components/UUDeclarationDialog";
import YWCTDeclarationDialog from "@/components/YWCTDeclarationDialog";
import CadentGasDialog from "@/components/CadentGasDialog";

import {
  DocumentsPageService,
  LenderService,
  OnboardingService,
} from "@/shared/api/services";
import { LenderParentRecord } from "@/shared/api/services/lenderService";
import { formatDate } from "@/shared/lib/utils/formatDate";
import { getCustomerId } from "@/shared/lib/utils/getCustomerId";
import { Checkbox as CheckboxMui, CircularProgress } from "@mui/material";
import { useAlert } from "@/app/providers/AlertProvider";
import { ThemeColours } from "@/clients/acme/config/colours";
import InfoDialog from "@/components/InfoDialog";
import placeholderIllustration from "@/assets/ai-answers.svg";
import { trackMetaEvent } from "@/shared/tracking/metaTracking";
import {
  getCompanyDocs,
  isSameDay,
  sortBySourceAndCreationDate,
} from "@/shared/share/sharePageUtils";

// ==================== Type Definitions ====================

export interface Document {
  secondId?: string;
  id: string;
  name: string;
  size: string;
  uploadedDate: string;
  type: "wage-slip" | "bank-statement" | "other";
  required?: boolean;
  raw?: SharedDocResponse;
  isNewlyUploaded?: boolean;
  formData?: FormData;
  description?: string;
  documentId?: string | number;
}

export interface NdlData {
  preferredTimeForNdl?: string;
  debtAdviceContactPreferenceNdl?: string;
  voicemailConsentNdl?: boolean;
  emailMeNdl?: boolean;
  textMeNdl?: boolean;
  customerPrefrences?: any;
  isPreferencesLoaded?: boolean;
}

export interface PaymentOfferData {
  paymentOfferAmount: string | number;
  paymentPreferredFrequency: string | number;
}

type DeclarationStatusType = "not-completed" | "accepted" | "rejected";

export interface Company {
  notSupported?: boolean;
  declarationData: CustomerDeclarationEntry | DeclarationAllEntry | undefined;
  id: string;
  name: string;
  reference: string;
  type:
    | "referred"
    | "debt"
    | "additional-support"
    | "referred-linked"
    | "added"
    | "all";
  documents: Document[];
  selectedDocuments: string[];
  lastShared?: string;
  address?: string;
  raw?: CustomerSharingResponse;
  isFp?: boolean;
  declarationStatus?: DeclarationStatusType;
  ndlData?: NdlData;
  paymentOfferData?: PaymentOfferData;
  applianceCount?: number;
  disableShare?: boolean;
  agentSubmitted?: boolean;
}

interface HouseholdDeclaration {
  status: "yes" | "no";
  circumstances: "yes" | "no";
  changeInCircumstances?: string;
  negativeCircumstanceChange?: string;
  previousCredit: "yes" | "no";
  financialPositionChange: "yes" | "no" | "not-applicable";
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  uploadDate: Date;
  file: File;
}

type TabType =
  | "all"
  | "referred"
  | "referred-linked"
  | "debts"
  | "additional-support"
  | "added";

// ==================== Component ====================

const Transition = React.forwardRef<
  unknown,
  { in?: boolean; children: React.ReactElement }
>(function Transition(props, ref) {
  return <Slide direction="up" ref={ref as React.Ref<unknown>} {...props} />;
});

const SharePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { addAlert } = useUI();
  const { showAlert } = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAdditionalSupportOpen, setIsAdditionalSupportOpen] =
    useState<boolean>(() => {
      // Default open unless explicitly disabled via query
      return searchParams.get("showAdditionalSupport") !== "false";
    });
  const [hasMobileNumber, setHasMobileNumber] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [debts, setDebts] = useState<[]>([]);

  const [emailAddress, setEmailAddress] = useState("");

  const [companyLastSharedDate, setCompanyLastSharedDate] = useState<number>(0);
  const [docsCreationDate, setDocsCreationDate] = useState<number>(0);
  const [debtsCreationDate, setDebtsCreationDate] = useState<number>(0);

  // Legacy parity: fire Meta Pixel + CAPI event when Share screen is opened
  useEffect(() => {
    void trackMetaEvent("InitiateCheckout", "InitiateCheckout");
  }, []);

  const [companyShareLastUpdateDate, setCompanyShareLastUpdateDate] =
    useState<number>(0);
  const [freePostLastUpdateDate, setFreePostLastUpdateDate] =
    useState<number>(0);

  const [freePostLastSharedDate, setFreePostLastSharedDate] =
    useState<number>(0);

  const [freepostCreditorNamesData, setFreepostCreditorNamesData] = useState(
    [],
  );

    const [loadedFreepostCreditorNamesData, setLoadedFreepostCreditorNamesData] = useState(false);


  const [cadentData, setCadentData] = useState([]);

  const [latestSnapshot, setLatestSnapshot] = useState<CustomerLatestSnapshot>({
    creationDate: "",
    lastUpdateDate: "",
    createdBy: 0,
    lastUpdatedBy: 0,
    id: 0,
    customerId: 0,
    balanceAmount: 0,
  });

  const [summary, setSummary] = useState<CalculatedTotals>({
    customerId: 0,
    totalIncome: 0,
    totalAssets: 0,
    totalOutgoingsFixed: 0,
    totalOutgoingsFlexible: 0,
    totalOwingOnPriorityDebt: 0,
    totalRepayOnPriorityDebt: 0,
    totalOwingOnNonPriorityDebt: 0,
    totalRepayOnNonPriorityDebt: 0,
    pendingPaymentOffer: 0,
    balanceAmount: 0,
    totalRepaymentDebts: 0,
    totalOwingOnDebts: 0,
  });

  const [isSummaryLoaded, setIsSummaryLoaded] = useState(false);
  const [hasCompaniesLoaded, setHasCompaniesLoaded] = useState(false);

  const [declarationData, setDeclarationData] = useState<
    CustomerDeclarationEntry[]
  >([]);
  const [declarationAllData, setDeclarationAllData] = useState<
    DeclarationAllEntry[]
  >([]);

  const [digitalLenders, setDigitalLenders] = useState<DigitalLender[]>([
    {
      schemaName: "",
      creditor: "",
      subDomain: "",
      lender: "",
      referenceNumberFormat: "",
      customerPaymentOffer: "",
      showCompanyList: "",
      addToDebtList: "",
      parentCompany: "",
      creditorAddress: "",
      isCreditorExistsWithIeHub: "",
    },
  ]);

  const setCustomerPrefrences = (
    sharingId: string | number,
    obj: Record<string, any>,
  ) => {
    setCompanies((prev: any) =>
      prev.map((company: any) =>
        company.id === sharingId
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                customerPrefrences: obj,
              },
            }
          : company,
      ),
    );
  };

  const setIsPreferencesLoaded = (
    sharingId: string | number,
    choice: boolean,
  ) => {
    setCompanies((prev: any) =>
      prev.map((company: any) =>
        company.id === sharingId
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                isPreferencesLoaded: choice,
              },
            }
          : company,
      ),
    );
  };

  const setPreferredTimeForNdl = (sharingId: string | number, data: string) => {
    setCompanies((prev: any) =>
      prev.map((company: any) =>
        company.id === sharingId
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                preferredTimeForNdl: data,
              },
            }
          : company,
      ),
    );
  };

  const setDebtAdviceContactPreferenceNdl = (
    sharingId: string | number,
    data: string,
  ) => {
    setCompanies((prev: any) =>
      prev.map((company: any) =>
        company.id === sharingId
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                debtAdviceContactPreferenceNdl: data,
              },
            }
          : company,
      ),
    );
  };

  const setVoicemailConsentNdl = (
    sharingId: string | number,
    data: boolean,
  ) => {
    setCompanies((prev: any) =>
      prev.map((company: any) =>
        company.id === sharingId
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                voicemailConsentNdl: data,
              },
            }
          : company,
      ),
    );
  };

  const setEmailMeNdl = (sharingId: string | number, choice: boolean) => {
    setCompanies((prev: any) =>
      prev.map((company: any) => {
        return company.id === parseInt(sharingId)
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                emailMeNdl: choice,
              },
            }
          : company;
      }),
    );
  };

  const setTextMeNdl = (sharingId: string | number, choice: boolean) => {
    setCompanies((prev: any) =>
      prev.map((company: any) =>
        company.id === sharingId
          ? {
              ...company,
              ndlData: {
                ...company.ndlData,
                textMeNdl: choice,
              },
            }
          : company,
      ),
    );
  };

  const [ieMstrLenderData, setIeMstrLenderData] = useState<
    LenderParentRecord[]
  >([
    {
      id: 0,
      parentCompany: "",
      description: "",
      showCompanyList: false,
      referralCompanies: false,
      customerPaymentOffer: false,
      addToDebtList: false,
      addToShareListFromProfile: false,
      referenceNumberFormat: "",
      childCompany: [],
      raw: { id: 0, lender: "" },
      childrenRaw: [],
    },
  ]);

  const [ieMstrLenderDataLoaded, setIeMstrLenderDataLoaded] = useState(false);

  // Section refs for scrolling
  const allSectionRef = useRef<HTMLDivElement>(null);
  const referredSectionRef = useRef<HTMLDivElement>(null);
  const debtsSectionRef = useRef<HTMLDivElement>(null);
  const additionalSupportSectionRef = useRef<HTMLDivElement>(null);
  const addedSectionRef = useRef<HTMLDivElement>(null);

  // ==================== State Management ====================
  const [activeTab, setActiveTab] = useState<TabType>("debts");
  const [digitalData, setDigitalData] = useState<ApiResponse<any[]>>();

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [openUUDeclarationDialog, setOpenUUDeclarationDialog] = useState(false);
  const [openYWCTDeclarationDialog, setOpenYWCTDeclarationDialog] =
    useState(false);
  const [openCadentGasDialog, setOpenCadentGasDialog] = useState(false);

  const [declarationCompany, setDeclarationCompany] = useState<Company>({
    id: "",
    name: "",
    reference: "",
    type: "debt",
    documents: [],
    selectedDocuments: [],
  });

  const [selectedEditCompany, setSelectedEditCompany] = useState<Company>({
    id: "",
    name: "",
    reference: "",
    type: "debt",
    documents: [],
    selectedDocuments: [],
  });

  const [companies, setCompanies] = useState<Company[]>([]);

  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    if (customerData.mobileNumber) {
      setHasMobileNumber(true);
    }
  }, []);

  // GET Cadent Gas Data
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getCadentGasData() {
      try {
        const response = await httpGet<ApplianceData[]>(
          sharePageEndpoints.getCadentAdditionalQuestions(
            customerData.customerId,
          ),
        );

        const res: any = response?.data;

        setCadentData(res);
      } catch (error) {
        femaCapture("fetching_cadent_gas_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getCadentGasData();
  }, []);

  // SET email address
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    setEmailAddress(customerData.emailAddress);
  }, []);

  // Fetch documents
  useEffect(() => {
    const fetchDocuments = async (): Promise<ServiceResponse<any[]>> => {
      let resolvedCustomerId: string | number | null = await getCustomerId();

      if (!resolvedCustomerId) {
        addAlert({
          severity: "warning",
          message:
            "Missing customerId. Add ?customerId= in URL or ensure you are logged in.",
          autoHide: true,
          duration: 5000,
        });
        return {
          data: [],
          success: false,
          timestamp: new Date().toISOString(),
        };
      }

      try {
        const data =
          await DocumentsPageService.getCustomerDocuments(resolvedCustomerId);

        return data;
      } catch (error) {
        console.error("Failed to fetch companies", error);
        return {
          data: [],
          success: false,
          timestamp: new Date().toISOString(),
        };
      }
    };

    fetchDocuments().then(async (documentsArrayPromise) => {
      const documentsArray = await documentsArrayPromise;

      if (documentsArray && Array.isArray(documentsArray)) {
        for (const doc of documentsArray) {
          let docsCreationDate = 0;

          docsCreationDate = Math.max(
            docsCreationDate,
            new Date(doc.creationDate).getTime(),
          );

          setDocsCreationDate(docsCreationDate);
        }
      }
    });
  }, []);

  const hasFetchedDebts = useRef(false);

  // GET DEBTS
  useEffect(() => {
    if (hasFetchedDebts.current) return;
    hasFetchedDebts.current = true;

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getDebts() {
      const debts = await OnboardingService.getDebts(
        Number(customerData.customerId),
        {
          skipErrorCapture: true,
        },
      );
      return debts;
    }

    getDebts().then((debts) => {
      let debtsCreationDate = 0;

      for (const debt of debts) {
        if (!debt.creationDate && !debt.lastUpdateDate) continue;

        debtsCreationDate = Math.max(
          debtsCreationDate,
          new Date(debt.creationDate || debt.lastUpdateDate).getTime(),
        );
      }

      setDebtsCreationDate(debtsCreationDate);
      setDebts(debts);
    });
  }, []);

  // GET declaration data all
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getDeclarationDataAll() {
      try {
        const response = await httpGet<ApiResponse>(
          declarationEndpoints.getDeclarationAll(customerData.customerId),
        );

        const data: any = response?.data;

        setDeclarationAllData(data);
      } catch (error) {
        femaCapture("fetching_declaration_all_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getDeclarationDataAll();
  }, []);

  // GET Freepost Creditor List Names
  // Lift state for add company dialog
  useEffect(() => {
    async function getFreepostCreditorNames(): Promise<ServiceResponse<any[]>> {
      try {
        const response = await httpGet<ApiResponse>(
          customerEndpoints.listFreepostLenders,
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_freepost_creditor_list_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getFreepostCreditorNames().then((data: any) => {
      setFreepostCreditorNamesData(data);
      setLoadedFreepostCreditorNamesData(true);
    });
  }, []);

  const hasFetchedSummary = useRef(false);

  // GET summary
  useEffect(() => {
    if (hasFetchedSummary.current) return;
    hasFetchedSummary.current = true;

    async function getSummary(): Promise<ServiceResponse<any[]>> {
      try {
        const resolvedCustomerId: string | number | null =
          await getCustomerId();

        const response = await httpGet<ApiResponse>(
          customerEndpoints.getCalculatedTotals(Number(resolvedCustomerId)),
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_calculated_totals_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getSummary().then((res: any) => {
      setSummary(res[0]);
      setIsSummaryLoaded(true);
    });
  }, []);

  // GET latest snapshot
  useEffect(() => {
    async function getLatestSnapshot(): Promise<ServiceResponse<any[]>> {
      try {
        const resolvedCustomerId: string | number | null =
          await getCustomerId();

        const response = await httpGet<ApiResponse>(
          sharePageEndpoints.getCustomerLatestSnapshot(
            Number(resolvedCustomerId),
          ),
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_latest-snapshot_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getLatestSnapshot().then((res: any) => {
      setLatestSnapshot(res[0]);
    });
  }, []);

  // GET declaration data
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getDeclarationData() {
      try {
        const response = await httpGet<ApiResponse>(
          declarationEndpoints.getDeclaration(customerData.customerId),
        );

        const data: any = response?.data;

        setDeclarationData(data);
        setHasLoadedDeclarationData(true);
      } catch (error) {
        femaCapture("fetching_declaration_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getDeclarationData();
  }, []);

  const hasFetchedCompanies = useRef(false);

  // FETCH companies data
  useEffect(() => {
    if (
      hasFetchedCompanies.current ||
      !hasLoadedDeclarationData ||
      !debts ||
      !isSummaryLoaded ||
      !ieMstrLenderDataLoaded || 
      !loadedFreepostCreditorNamesData
    )
      return;

    hasFetchedCompanies.current = true;

    const fetchCompanies = async (): Promise<Company[]> => {
      const resolvedCustomerId: string | number | null = await getCustomerId();

      if (!resolvedCustomerId) {
        addAlert({
          severity: "warning",
          message:
            "Missing customerId. Add ?customerId= in URL or ensure you are logged in.",
          autoHide: true,
          duration: 5000,
        });
        return [];
      }

      try {
        const coerceNumericId = (value: unknown): number | null => {
          if (typeof value === "number" && Number.isFinite(value)) return value;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed) return null;
            const parsed = Number(trimmed);
            return Number.isFinite(parsed) ? parsed : null;
          }
          return null;
        };

        const isSharedRow = (row: any): boolean => {
          const submit =
            typeof row?.submitDate === "string" ? row.submitDate.trim() : "";
          const lastShared =
            typeof row?.lastSharedDate === "string"
              ? row.lastSharedDate.trim()
              : "";
          return Boolean(submit || lastShared);
        };

        /**
         * IMPORTANT (legacy parity):
         * - Keep ALL rows that were already shared (submitDate/lastSharedDate present),
         *   because history must remain visible (and reference edits after sharing create a new row).
         * - Only dedupe *pending/unshared* duplicates (no submitDate/lastSharedDate), since these
         *   are the "company shows twice" bug scenario.
         */
        const dedupeSharingRows = (rows: any[]): any[] => {
          const bestPendingByKey = new Map<string, any>();

          const keyForRow = (row: any): string => {
            const debtId = coerceNumericId(row?.debtId);
            const id = coerceNumericId(row?.id);
            if (typeof debtId === "number") return `debt:${debtId}`;
            if (typeof id === "number") return `id:${id}`;
            return `fallback:${String(row?.lenderName ?? row?.companyName ?? "")}|${String(
              row?.accountNumber ?? row?.referenceNumber ?? "",
            )}`;
          };

          // Pass 1: choose the "best" pending row per key (latest lastUpdateDate)
          for (const row of rows) {
            if (isSharedRow(row)) continue;
            const key = keyForRow(row);
            const prev = bestPendingByKey.get(key);
            const prevTs = prev?.lastUpdateDate
              ? Date.parse(prev.lastUpdateDate)
              : NaN;
            const nextTs = row?.lastUpdateDate
              ? Date.parse(row.lastUpdateDate)
              : NaN;
            if (
              !prev ||
              (Number.isFinite(nextTs) &&
                (!Number.isFinite(prevTs) || nextTs >= prevTs))
            ) {
              bestPendingByKey.set(key, row);
            }
          }

          // Pass 2: rebuild list preserving original order:
          // - include all shared rows
          // - include only the chosen pending row per key
          const emittedPendingKeys = new Set<string>();
          const out: any[] = [];
          for (const row of rows) {
            if (isSharedRow(row)) {
              out.push(row);
              continue;
            }
            const key = keyForRow(row);
            if (emittedPendingKeys.has(key)) continue;
            if (bestPendingByKey.get(key) === row) {
              out.push(row);
              emittedPendingKeys.add(key);
            }
          }
          return out;
        };

        const digitalDataRaw: any =
          await SharePageService.getCustomerSharing(resolvedCustomerId);
        const digitalData: any[] = Array.isArray(digitalDataRaw)
          ? dedupeSharingRows(digitalDataRaw)
          : [];

        let localCompanyShareLastUpdateDate = 0;

        let companyLastSharedDate = null;

        for (const company of digitalData) {
          localCompanyShareLastUpdateDate = Math.max(
            localCompanyShareLastUpdateDate,
            new Date(company.lastUpdateDate).getTime(),
          );

          setCompanyShareLastUpdateDate(localCompanyShareLastUpdateDate);

          const updateTime = new Date(company.lastUpdateDate).getTime();
          companyLastSharedDate =
            companyLastSharedDate === null
              ? updateTime
              : Math.max(companyLastSharedDate, updateTime);
        }

        setCompanyLastSharedDate(companyLastSharedDate as number);

        setDigitalData(digitalData);

        const customerData = JSON.parse(
          localStorage.getItem("customerData") as string,
        );

        const freePostDataRaw: any = await SharePageService.getFreePostData(
          customerData.customerId,
        );
        const freePostData: any[] = Array.isArray(freePostDataRaw)
          ? dedupeSharingRows(freePostDataRaw)
          : [];

        let freePostLastSharedDate = null;

        for (const company of freePostData) {
          let freePostLastUpdateDate = 0;

          freePostLastUpdateDate = Math.max(
            freePostLastUpdateDate,
            new Date(company.lastUpdateDate).getTime(),
          );

          setFreePostLastUpdateDate(freePostLastUpdateDate);

          const updateTime = new Date(company.lastUpdateDate).getTime();

          freePostLastSharedDate =
            freePostLastSharedDate === null
              ? updateTime
              : Math.max(freePostLastSharedDate, updateTime);
        }

        setFreePostLastSharedDate(freePostLastSharedDate as number);

        const canShareAgain = await shareBudgetsLogic(
          companyLastSharedDate,
          freePostLastSharedDate,
        );

        console.log("shareDate", "csa", canShareAgain);

        const data = [
          ...(await Promise.all(
            digitalData.map(async (company: any) => {
              if (!company.lenderName) {
                return {};
              }

              // const correspondingDebt = debts.find((entry: any) => {
              //   return entry.id == company.debtId;
              // });

              let type = "all";

              const obj: Company = {
                id: company.id,
                name: company.lenderName,
                reference: company.accountNumber,
                type: type as Company["type"],
                address: company.address || "",
                documents: company.documents || [],
                selectedDocuments: [],
                lastShared: company.lastShared || "",
                raw: company,
              };

              obj.agentSubmitted = (obj.raw?.submitDate && !obj.raw?.lastSharedDate) as boolean;

              const shareDate = company.lastSharedDate || company.submitDate;

              // Auto-disable within 24h cooldown, but re-enable if new changes detected
              if (shareDate && isSameDay(shareDate) && !canShareAgain) {
                if (!obj.agentSubmitted) {
                  obj.disableShare = true;
                  if (obj.raw) {
                    obj.raw.shareNow = "N";
                  }
                }
              } else if (canShareAgain && obj.disableShare) {
                obj.disableShare = false;
              } else if (canShareAgain && obj.raw?.shareNow === "N") {
                // re-enable share/declaration actions when budget/doc updates allow
                //obj.raw.shareNow = "Y"; - Causing side effects so commenting out
              }

              if (company.lenderName == "National Debtline") {
                obj.ndlData = {
                  debtAdviceContactPreferenceNdl: "Y",
                  emailMeNdl: true,
                  textMeNdl: hasMobileNumber,
                };
              }

              if (company.lenderName == "United Utilities") {
                const companyDeclarationData = declarationData.find((entry) => {
                  return entry.sharingId == company.id;
                });

                let declarationStatus: DeclarationStatusType =
                  companyDeclarationData?.declarationStatus == "Accept"
                    ? "accepted"
                    : companyDeclarationData?.declarationStatus == "Reject"
                      ? "rejected"
                      : "not-completed";

                obj.declarationStatus = declarationStatus;
                obj.declarationData = companyDeclarationData;
                obj.declarationStatus == "rejected"
                  ? (obj.raw.shareNow = "N")
                  : null;
              } else if (
                company.lenderName == "Yorkshire Water Community Trust"
              ) {
                const companyDeclarationData = declarationAllData.find(
                  (entry) => {
                    return entry.sharingId == company.id;
                  },
                );

                let declarationStatus: DeclarationStatusType =
                  companyDeclarationData?.declarationStatus == "Accept"
                    ? "accepted"
                    : companyDeclarationData?.declarationStatus == "Reject"
                      ? "rejected"
                      : "not-completed";

                obj.declarationData = companyDeclarationData;
                obj.declarationStatus = declarationStatus;
              } else if (company.lenderName == "Cadent Gas") {
                const data = cadentData.find((entry: ApplianceData) => {
                  return company.id == entry.sharingId;
                });

                let applianceCount = 0;

                if (data) {
                  const fieldsToCheck = [
                    "locationBoller",
                    "locationGasCooker",
                    "locationGasFire",
                    "locationGasHob",
                  ];

                  for (const field of fieldsToCheck) {
                    if (data[field]) {
                      applianceCount += 1;
                    }
                  }
                }

                obj.applianceCount = applianceCount;
              }

              try {
                const docsResponse = await getCompanyDocs(
                  company.id,
                  company.lenderName,
                  company.accountNumber,
                );
                const docsData = await docsResponse;

                // Normalize possible shapes: ServiceResponse[], ApiResponse, raw array
                const docsArray: SharedDocResponse[] = Array.isArray(
                  (docsData as any)?.data,
                )
                  ? ((docsData as any)?.data as SharedDocResponse[])
                  : Array.isArray((docsData as any)?.data?.data)
                    ? ((docsData as any)?.data?.data as SharedDocResponse[])
                    : Array.isArray(docsData)
                      ? (docsData as SharedDocResponse[])
                      : [];

                const documents: Document[] = docsArray.map(
                  (doc: SharedDocResponse) => ({
                    id: String(doc.id),
                    name: doc.documentName || doc.fileName || "Document",
                    size: doc.documentSize || "",
                    type: "wage-slip",
                    uploadedDate: doc.lastUpdateDate
                      ? formatDate(doc.lastUpdateDate)
                      : "",
                    raw: doc,
                  }),
                );

                obj.documents = documents;

                let selectedDocuments: string[] = [];

                for (const doc of documents) {
                  if (doc?.raw?.isShared == "Y") {
                    selectedDocuments.push(doc.id);
                  }
                }

                obj.selectedDocuments = selectedDocuments;
              } catch (e) {
                // eslint-disable-next-line no-console
                console.debug(
                  "An error occurred when fetching company docs",
                  e,
                );
              }

              // fetch payment offer data for company
              // try {
              //   const response = await httpGet<any>(
              //     debtEndpoints.paymentOffer.getSingle(
              //       customerData.customerId,
              //       company.lenderName,
              //       company.accountNumber
              //     )
              //   );

              //   if (response.data.length > 0) {
              //     const { paymentOfferAmount, paymentPreferredFrequency } =
              //       response.data[0];

              //     obj.paymentOfferData = {
              //       paymentOfferAmount: paymentOfferAmount,
              //       paymentPreferredFrequency: ordinal(
              //         parseInt(paymentPreferredFrequency || "0")
              //       ),
              //     };
              //   }
              // } catch (e) {
              //   console.debug(
              //     "An error occurred when fetching payment offer data",
              //     e
              //   );
              // }

              return obj;
            }),
          )),

          ...freePostData.map((company: any) => {
            let type = "all";

            const shareDate = company.lastSharedDate || company.submitDate;

            const isFreepostCompanyInList = freepostCreditorNamesData.find((e: any)=> e.creditor == company.companyName);

            let obj: Company = {
              id: company.id,
              name: company.companyName,
              reference: company.referenceNumber,
              type: type as Company["type"],
              address: company.address || "",
              documents: company.documents || [],
              selectedDocuments: [],
              lastShared: company.lastShared || "",
              raw: company,
              isFp: true,
              notSupported: !isFreepostCompanyInList,
            };

            console.log(
              "shareDate",
              shareDate,
              company,
              isWithin24Hours(shareDate),
              canShareAgain,
            );

            // Auto-disable within 24h cooldown, but re-enable if new changes detected
            if (shareDate && isSameDay(shareDate) && !canShareAgain) {
              obj.disableShare = true;
              if (obj.raw) {
                obj.raw.shareNow = "N";
              }
            } else if (canShareAgain && obj.disableShare) {
              obj.disableShare = false;
            } else if (canShareAgain && obj.raw?.shareNow === "N") {
              //obj.raw.shareNow = "Y"; - Causing side effects so commenting out
            }

            if (obj.notSupported) {
               obj.disableShare = true;
            }

            return obj;
          }),
        ];

        return data;
      } catch (error) {
        console.error("Failed to fetch companies", error);
        return [];
      }
    };

    fetchCompanies().then((companiesArray) => {
      setCompanies(sortBySourceAndCreationDate(companiesArray));
      setHasCompaniesLoaded(true);
    });
  }, [declarationData, debts, isSummaryLoaded, ieMstrLenderDataLoaded, loadedFreepostCreditorNamesData]);

  const [expandedCompanies] = useState<string[]>([]);
  const [declarationOpen, setDeclarationOpen] = useState(false);
  const [hasLoadedDeclarationData, setHasLoadedDeclarationData] =
    useState(false);

  const [householdDeclaration, setHouseholdDeclaration] =
    useState<HouseholdDeclaration>({
      status: "no",
      circumstances: "no",
      previousCredit: "no",
      financialPositionChange: "no",
    });
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [shareHistoryOpen, setShareHistoryOpen] = useState(false);

  // ==================== Computed Values ====================

  // Open dialog if URL contains additionSupport=true
  useEffect(() => {
    if (searchParams.get("additionSupport") === "true") {
      setDeclarationOpen(true);
    }
  }, [searchParams]);

  const hasFetchedPreferences = useRef(false);

  // GET customer preferences data
  useEffect(() => {
    if (!companies || companies.length == 0) {
      return;
    }

    if (hasFetchedPreferences.current) return;
    hasFetchedPreferences.current = true;

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getCustomerPreferencesData() {
      const { data } = await httpGet<CustomerPreferencesEntry[]>(
        customerEndpoints.getCustomerPreferences(customerData.customerId),
      );

      if (data.length > 0) {
        for (const entry of data) {
          const company = companies.find((c) => c.raw?.id == entry.sharingId);

          if (!company) {
            continue;
          }

          const obj = entry as CustomerPreferencesEntry;

          setCustomerPrefrences(company.raw?.id as number, obj);
          setIsPreferencesLoaded(company.raw?.id as number, true);

          setPreferredTimeForNdl(
            company.raw?.id as number,
            obj.preferredTimeForNdl,
          );
          // setDebtAdviceContactPreferenceNdl(
          //   company.raw?.id as number,
          //   obj.debtAdviceContactPreferenceNdl
          // );
          setVoicemailConsentNdl(
            company.raw?.id as number,
            obj.voicemailConsentNdl == "Y",
          );
          setEmailMeNdl(company.raw?.id as number, obj.emailMeNdl == "Y");
          setTextMeNdl(company.raw?.id as number, obj.textMeNdl == "Y");
        }
      }

      return;
    }

    getCustomerPreferencesData();
  }, [companies]);

  // GET lender data
  useEffect(() => {
    async function getCustomerShareData() {
      const { data } = await LenderService.fetchLenders({
        skipErrorCapture: true,
      });

      if (data) {
        setIeMstrLenderData(data);
      }
      setIeMstrLenderDataLoaded(true);
    }

    getCustomerShareData();
  }, []);

  // GET Digital Creditor List Names
  useEffect(() => {
    async function getCreditorNames(): Promise<void> {
      try {
        const response = await httpGet<ApiResponse>(
          customerEndpoints.listDigitalLenders,
        );

        if (response.data && Array.isArray(response.data)) {
          setDigitalLenders(response.data as DigitalLender[]);
        }
      } catch (error) {
        femaCapture("fetching_creditor_list_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
    }

    getCreditorNames();
  }, []);

  const groupedCompanies = useMemo(() => {
    const groups: Record<string, Company[]> = {
      debt: [],
      "additional-support": [],
      added: [],
      "referred-linked": [],
      referred: [],
      all: [],
    };

    companies.forEach((company) => {
      const group = groups[company.type];
      if (company.raw?.isSharing != "N" && company.raw?.isDeleted != "Y") {
        if (group) {
          group.push(company);
        }
      }
    });

    groups["debt"] = [
      ...(groups["referred-linked"] ?? []),
      ...(groups["debt"] ?? []),
    ];

    return groups;
  }, [companies]);

  // Calculate total unique companies being shared
  const totalCompaniesCount = useMemo(() => {
    const filteredCompanies = companies.filter(
      (c) =>
        c.raw?.isSharing !== "N" &&
        c.raw?.isDeleted !== "Y" &&
        c.raw?.shareNow == "Y",
    );
    const uniqueNames = new Set(filteredCompanies.map((c) => c.name));
    return uniqueNames.size;
  }, [companies]);

  const allCOunt = useMemo(
    () =>
      companies.filter(
        (c) =>
          c.type == "all" && c.raw?.isDeleted != "Y" && c.raw?.isSharing == "Y",
      ).length,
    [companies],
  );

  const hasNoCompanies = useMemo(() => {
    return [allCOunt].every((count) => count == 0 || !count);
  }, [allCOunt, companies]);

  useEffect(() => {
    setCompanies((prev) => sortBySourceAndCreationDate(prev));
  }, [companies.length]);

  // ==================== Event Handlers ====================

  // Scroll to section
  const scrollToSection = useCallback((section: TabType) => {
    let ref: React.RefObject<HTMLDivElement> | null = null;

    switch (section) {
      case "all":
        ref = allSectionRef;
        break;
      case "referred":
        ref = referredSectionRef;
        break;
      case "debts":
        ref = debtsSectionRef;
        break;
      case "additional-support":
        ref = additionalSupportSectionRef;
        break;
      case "added":
        ref = addedSectionRef;
        break;
    }

    if (ref?.current) {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      const elm = document.getElementById(`${section}-section`);
      if (elm) elm.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const handleTabChange = useCallback(
    (tab: TabType) => {
      setActiveTab(tab);
      scrollToSection(tab);
    },
    [scrollToSection],
  );

  const toggleEditCompanyDialog = (company: Company) => {
    setSelectedEditCompany(company);
    setOpenEditDialog(true);
  };

  const toggleDeleteCompanyDialog = (company: Company) => {
    setSelectedEditCompany(company);
    setOpenDeleteDialog(true);
  };

  const toggleInfoDialog = (company: Company) => {
    setSelectedEditCompany(company);
    setOpenInfoDialog(true);
  };

  const handleDocumentToggle = useCallback(
    (companyId: string, documentId: string, findId?: boolean) => {
      if (findId) {
        documentId =
          companies
            .find((c) => c.id == companyId)
            ?.documents.find((d) => d.raw?.documentId == parseInt(documentId))
            .id || "0";
      }

      console.log(companyId, documentId);

      setCompanies((prev) =>
        prev.map((company) => {
          if (company.id === companyId) {
            const isSelected = company.selectedDocuments.includes(documentId);

            const document = company.documents.find(
              (entry) => entry.id == documentId,
            );

            const isShared = isSelected ? "N" : "Y";

            const updateSharingDoc = async (payload: SharedDocResponse) => {
              await httpPut<CustomerSharingResponse>(
                documentsPageEndpoints.updateSharedDocuments(payload.id),
                payload,
              );
            };

            updateSharingDoc({
              ...(document?.raw as SharedDocResponse),
              isShared,
            });

            // set declaraton data selected documents for UU document checking functionality
            if (company.id == declarationCompany.id) {
              setDeclarationCompany({
                ...declarationCompany,
                selectedDocuments: isSelected
                  ? company.selectedDocuments.filter((id) => id !== documentId)
                  : [...company.selectedDocuments, documentId],
              });
            }

            return {
              ...company,
              selectedDocuments: isSelected
                ? company.selectedDocuments.filter((id) => id !== documentId)
                : [...company.selectedDocuments, documentId],
            };
          }
          return company;
        }),
      );
    },
    [companies, declarationCompany],
  );

  const handleFileUpload = useCallback((files: FileList | null) => {
    if (!files) return;

    const newFiles: UploadedFile[] = Array.from(files).map((file) => ({
      id: `file-${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      uploadDate: new Date(),
      file,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      handleFileUpload(e.dataTransfer.files);
    },
    [handleFileUpload],
  );

  const handleRemoveFile = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const handleDeclarationClose = useCallback(() => {
    setDeclarationOpen(false);
    const next = new URLSearchParams(searchParams);
    next.delete("additionSupport");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleDeclarationSubmit = useCallback(() => {
    setDeclarationOpen(false);
    const next = new URLSearchParams(searchParams);
    next.delete("additionSupport");
    setSearchParams(next, { replace: true });
  }, [
    householdDeclaration,
    uploadedFiles,
    companies,
    searchParams,
    setSearchParams,
  ]);

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }, []);

  // ==================== Render Functions ====================

  const renderHeader = () => (
    <Box sx={{ mb: isMobile ? 3 : 4 }}>
      <Typography
        variant={isMobile ? "h5" : "h4"}
        sx={{
          fontWeight: 600,
          color: theme.palette.primary.main,
          mb: 2,
        }}
      >
        Share Your Budget
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: theme.palette.text.secondary,
          mb: 3,
          lineHeight: 1.6,
        }}
      >
        Manage your budget sharing with financial institutions and support
        organizations. Review, share, and track your submissions all in one
        place.
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          color: theme.palette.primary.main,
          "&:hover": {
            textDecoration: "underline",
          },
        }}
        // onClick={() => setDeclarationOpen(true)}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            color: "#155F54",
            textDecoration: "underline",
          }}
          onClick={() => {
            setIsAdditionalSupportOpen(true);
          }}
        >
          Check Your Support Options
        </Typography>
        <ArrowForwardIcon sx={{ fontSize: 22 }} />
      </Box>
    </Box>
  );

  const renderTabs = () => (
    <>
      <Box
        sx={{
          display: "flex",
          gap: isMobile ? 1 : 2,
          mb: 3,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
          <Box
            onClick={() => {
              setShareHistoryOpen(true);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "white",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
              p: 1.5,
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <HistoryIcon />
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, color: "#495057" }}
            >
              Share History
            </Typography>
          </Box>

          <Box
            onClick={() => setAddCompanyOpen(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              flex: 1, // take remaining width
              backgroundColor: "#DFE7E2",
              border: "2px dashed #7BA39B", // dash color
              borderRadius: 1.5,
              cursor: "pointer",
              color: theme.palette.primary.main,
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#cfd8cc",
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                textAlign: "center",
                width: "100%",
              }}
            >
              + Add Company
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );

  async function toggleCompanyShare(company: Company) {
    const newCompany = {
      ...company,
      raw: {
        ...company.raw,
        shareNow: company.raw?.shareNow == "Y" ? "N" : "Y",
      },
    };

    // if (!company.isFp) {
    //   newCompany.raw.isSharing = company.raw?.shareNow == "Y" ? "N" : "Y";
    // }

    setCompanies(
      companies.map((entry: any) => {
        if (entry.id == company.id) {
          entry = newCompany;
        }

        return entry;
      }),
    );

    let newResp;

    if (!company.isFp) {
      newResp = await httpPut<CustomerSharingResponse>(
        sharePageEndpoints.createCustomerSharing(),
        newCompany.raw,
      );
    } else {
      newResp = await httpPut<CustomerSharingResponse>(
        sharePageEndpoints.createFreepostSharing(),
        newCompany.raw,
      );
    }

    if (company.raw?.shareNow == "N") {
      setCompanyShareLastUpdateDate(
        new Date(
          newResp.data.lastUpdateDate || companyShareLastUpdateDate,
        ).getTime(),
      );
    }
  }

  function isShareCooldownOver(
    companyLastSharedDateL?: number,
    freePostLastSharedDateL?: number,
  ) {
    const now = Date.now();

    // Use the local arguments if provided, otherwise fallback to outer scoped variables
    const companyDate = companyLastSharedDateL ?? companyLastSharedDate;
    const freePostDate = freePostLastSharedDateL ?? freePostLastSharedDate;

    const companyOk =
      !companyDate || now > new Date(companyDate).setHours(23, 59, 59, 999);

    // eslint-disable-next-line no-console
    console.debug(
      companyOk
        ? "Company share cooldown OK"
        : "Company share cooldown NOT over",
    );

    const freePostOk =
      !freePostDate || now > new Date(freePostDate).setHours(23, 59, 59, 999);

    // eslint-disable-next-line no-console
    console.debug(
      freePostOk
        ? "Free post share cooldown OK"
        : "Free post share cooldown NOT over",
    );

    const result = companyOk && freePostOk;

    // eslint-disable-next-line no-console
    console.debug(
      result
        ? "Cooldown IS over: all required conditions met"
        : "Cooldown NOT over: one or more conditions failed",
    );

    // eslint-disable-next-line no-console
    console.debug("cooldown", companyDate, freePostDate, Date.now());

    return result;
  }

  async function shareBudgetsLogic(
    companyLastSharedDateL?: number,
    freePostLastSharedDateL?: number,
  ) {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    customerData.submitDate =
      (localStorage.getItem("submitDate") as string) || customerData.submitDate;

    // eslint-disable-next-line no-console
    console.debug(
      "submitDate sbl:::",
      customerData.submitDate == null || !customerData.submitDate,
    );

    if (customerData.submitDate == null || !customerData.submitDate) {
      return true;
    }

    // eslint-disable-next-line no-console
    console.debug(
      "customerData",
      customerData,
      localStorage.getItem("submitDate"),
    );

    const submitDate = new Date(customerData.submitDate);
    const milliseconds = submitDate.getTime();

    let submitDateAddedMilliSecs = new Date(customerData.submitDate);
    submitDateAddedMilliSecs.setTime(submitDateAddedMilliSecs.getTime() + 5000);
    const millisecondsAfterAdded = submitDateAddedMilliSecs.getTime();

    // Create a new Date object for 11:59:59.999 PM of the same day
    if (isShareCooldownOver(companyLastSharedDateL, freePostLastSharedDateL)) {
      return true;
    }

    const prevBalanceAmount = latestSnapshot?.balanceAmount || 0;

    // eslint-disable-next-line no-console
    console.debug(
      "summary.balanceAmount != prevBalanceAmount",
      summary.balanceAmount != prevBalanceAmount,
    );

    if (summary.balanceAmount != prevBalanceAmount) {
      return true;
    }

    // eslint-disable-next-line no-console
    console.debug(
      "companyShareLastUpdateDate-pre",
      companyShareLastUpdateDate,
      docsCreationDate,
      freePostLastUpdateDate,
      debtsCreationDate,
      milliseconds,
      millisecondsAfterAdded,
    );

    /* eslint-disable no-console */
    console.debug("c1", companyShareLastUpdateDate > millisecondsAfterAdded);
    console.debug("c2", docsCreationDate > milliseconds);
    console.debug("c3", debtsCreationDate > milliseconds);
    console.debug("c4", freePostLastUpdateDate > milliseconds);
    /* eslint-enable no-console */

    if (
      companyShareLastUpdateDate > millisecondsAfterAdded ||
      docsCreationDate > milliseconds ||
      debtsCreationDate > milliseconds ||
      freePostLastUpdateDate > milliseconds
    ) {
      return true;
    }

    // eslint-disable-next-line no-console
    console.debug(
      "companyShareLastUpdateDate",
      companyShareLastUpdateDate,
      docsCreationDate,
      freePostLastUpdateDate,
      milliseconds,
      millisecondsAfterAdded,
    );

    return false;
  }

  async function getLatestIeUpdateDate() {
    try {
      const resolvedCustomerId: string | number | null = await getCustomerId();

      const response = await httpGet<ApiResponse>(
        latestUpdateEndpoints.fetchLatestUpdate(Number(resolvedCustomerId)),
      );

      console.log("getLatestIeUpdateDate", response.data);
      return response.data;
    } catch (error) {
      femaCapture("fetching_latest-snapshot_data_error", error, {
        category: "api",
        severity: "error",
        skipSanitization: false,
        user: {},
      });
      throw error;
    }
  }

  const hasEnabledDeclaration = useRef(false);

  //Re-enable sharing/declaration after budget/doc/company updates pass cooldown
  useEffect(() => {
    if (
      hasEnabledDeclaration.current ||
      !hasLoadedDeclarationData ||
      !debts ||
      !isSummaryLoaded ||
      !ieMstrLenderDataLoaded ||
      !companies ||
      companies.length == 0
    ) {
      return;
    }

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    hasEnabledDeclaration.current = true;

    (async () => {
      setCompanies((prev) => {
        let changed = false;
        const next = prev.map((company) => {
          const shouldEnable =
            company.disableShare || company.raw?.shareNow === "N";

          // Always reset declaration when we can share again
          const shouldResetDeclaration =
            (company.name === "United Utilities" ||
              company.name === "Yorkshire Water Community Trust") &&
            company.declarationStatus === "accepted";

          const epochMilliseconds = company.declarationData
            ? (company.declarationData.customerAgreedDate ||
                company.declarationData.lastUpdateDate / 1000) * 1000
            : 0;

          const submitDateAddedMilliSecs = new Date(customerData.submitDate);
          submitDateAddedMilliSecs.setTime(
            submitDateAddedMilliSecs.getTime() + 5000,
          );

          const millisecondsAfterAdded = submitDateAddedMilliSecs.getTime();

          const prevBalanceAmount = latestSnapshot?.balanceAmount || 0;

          //getLatestIeUpdateDate();

          const canShareAgain =
            docsCreationDate > epochMilliseconds ||
            debtsCreationDate > epochMilliseconds //||
            //summary.balanceAmount != prevBalanceAmount;

          if (!canShareAgain) {
            return company;
          }

          if (!shouldResetDeclaration) {
            //const needsUpdate = shouldEnable && shouldResetDeclaration;

            return company;
          }

          const updated: Company = {
            ...company,
            ...(shouldEnable ? { disableShare: false } : {}),
            ...(company.raw
              ? {
                  raw: {
                    ...company.raw,
                    shareNow: "Y",
                  },
                }
              : {}),
            ...(shouldResetDeclaration
              ? {
                  declarationStatus: "not-completed" as DeclarationStatusType,
                }
              : {}),
          };

          changed = true;
          return updated;
        });

        return changed ? next : prev;
      });
    })();
  }, [
    hasEnabledDeclaration,
    hasLoadedDeclarationData,
    debts,
    isSummaryLoaded,
    ieMstrLenderDataLoaded,
    companies,
  ]);

  function getUniqueCompanieNames() {
    const seenNames: Set<string> = new Set();

    for (const company of companies) {
      const name = company.raw?.creditor || company.name;
      if (company.raw?.shareNow === "Y" && !seenNames.has(name)) {
        seenNames.add(name);
      }
    }

    // Convert Set to array before joining
    return Array.from(seenNames).join(", ");
  }

  function renderCompanyReference(reference: string) {
    return reference == emailAddress ? "" : reference;
  }

  async function shareData() {
    setIsSharing(true);
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    // Company Validation
    for (const company of companies) {
      if (company.raw?.isSharing == "N" || company.raw?.isDeleted == "Y ") {
        continue;
      }

      const declarationCompanies = [
        "United Utilities",
        "Yorkshire Water Community Trust",
      ];

      if (
        declarationCompanies.includes(company.name) &&
        company.declarationStatus != "accepted" &&
        company.raw?.shareNow == "Y"
      ) {
        showAlert({
          severity: "warning",
          message: `Please read and accept the declaration from ${company.name} to proceed.`,
          autoHide: true,
          duration: 2000,
        });

        setIsSharing(false);
        return;
      }

      console.log(
        "company.ndlData?.emailMeNdl",
        hasMobileNumber,
        company.ndlData?.emailMeNdl,
      );

      if (
        company.name == "National Debtline" &&
        company.raw?.shareNow == "Y" &&
        ((!hasMobileNumber && !company.ndlData?.emailMeNdl) ||
          (hasMobileNumber && !company.ndlData?.preferredTimeForNdl))
      ) {
        showAlert({
          severity: "warning",
          message: `Please answer all the mandatory fields for the company National Debtline.`,
          autoHide: true,
          duration: 2000,
        });

        setIsSharing(false);
        return;
      }
    }

    const getNotificationSource = (mobileNumber?: string) =>
      mobileNumber ? "sendSMS" : "sendEmail";

    for (const company of companies) {
      if (company.raw?.shareNow != "Y") {
        continue;
      }

      if (company.name === "National Debtline") {
        const payload: NotifyCustomerDebtAdvice = {
          companyName: "Bristol Water",
          userId: customerData.userId,
          isUnitedUtilitiesCompany: "N",
          mobileNumber: customerData.mobileNumber,
          notificationSource: getNotificationSource(customerData.mobileNumber),
          debtAdviceContactPreNdl:
            company.ndlData?.debtAdviceContactPreferenceNdl ?? "Y",
        };

        await httpPost<NotifyCustomerDebtAdviceResponse>(
          customerEndpoints.notifyCustomerDebtAdvice(),
          payload,
        );
        continue;
      }

      if (["PayPlan", "Make Life Better"].includes(company.name)) {
        const payload: NotifyCustomerDebtAdvice = {
          companyName: company.name,
          userId: customerData.userId,
          mobileNumber: customerData.mobileNumber,
          notificationSource: "sendEmail",
        };

        await httpPost<NotifyCustomerDebtAdviceResponse>(
          customerEndpoints.notifyCustomerDebtAdvice(),
          payload,
        );
      }

      if (company.name == "Cadent Gas") {
        const payload: SendEmailOnShareEntry = {
          companyName: company.name,
        };

        await httpPost<SendEmailOnShareEntry>(
          customerEndpoints.sendEmailOnShare(),
          payload,
        );
      }
    }

    const resp = await shareBudgetsLogic();

    const companyToShareCount = companies.filter(
      (company) =>
        company.raw?.shareNow == "Y" &&
        company.raw?.isDeleted !== "Y" &&
        company.raw?.isSharing !== "N",
    ).length;

    const hasDisabledShareCompanies = companies.filter(
      (company) =>
        company.disableShare &&
        company.raw?.isDeleted !== "Y" &&
        company.raw?.isSharing !== "N",
    ).length;

    if (hasDisabledShareCompanies && companyToShareCount == 0) {
      //alert("You've Already Shared Your Budget Today");

      localStorage.removeItem("companyList");

      console.log("companyToShareCount", companyToShareCount, resp);

      localStorage.setItem("alreadyShared", "Y");
      navigate("/share-success");
      setIsSharing(false);
      return;
    } else {
      localStorage.setItem("alreadyShared", "N");
    }

    // update balance amount
    if (latestSnapshot) {
      const payload = {
        ...latestSnapshot,
        balanceAmount: summary.balanceAmount,
      };

      // update existing snapshot data
      await httpPut<CustomerSharingResponse>(
        sharePageEndpoints.createCustomerLatestSnapshot(),
        payload,
      );
    } else {
      const payload = {
        balanceAmount: summary.balanceAmount,
        customerId: customerData.customerId,
      };

      await httpPost<CustomerSharingResponse>(
        sharePageEndpoints.createCustomerLatestSnapshot(),
        payload,
      );
    }

    let companyList = [];

    // send digital companies
    for (const company of companies) {
      try {
        if (
          company.raw?.shareNow == "Y" &&
          //!company.raw?.lastSharedDate &&
          company.raw?.isDeleted != "Y" &&
          company.raw?.isSharing != "N"
        ) {
          companyList.push(company.raw?.lenderName || company.raw?.companyName);
        }

        if (!company.isFp) {
          // const payload = {
          //   accountNumber: company.reference,
          //   customerId: customerData.customerId,
          //   lenderName: company.raw?.lenderName,
          //   orgId: 0,
          //   previousAccountNumber: company.reference,
          //   previousLenderName: company.raw?.lenderName,
          //   schemaName: company.raw?.schemaName,
          //   shareNow: company.raw?.shareNow,
          //   subDomain: company.raw?.subDomain,
          // };
          // await httpPost<CustomerSharingResponse>(
          //   sharePageEndpoints.insertCustomerSharingDetails(),
          //   payload
          // );
        }
      } catch (e) {
        femaCapture("sending_digital_company_error", e, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
    }

    localStorage.setItem("companyList", companyList.join(";"));

    // finally update customer profile

    if (companyToShareCount > 0) {
      customerData.status = "SUBMIT";
      //customerData.isApiCall = "Y";
      customerData.isSubmitted = "Y";
      customerData.lenderList = getUniqueCompanieNames();
      customerData.submitDate = new Date(Date.now() + 3000).toISOString();

      delete customerData.cachedAt;

      const newCustomerData = await httpPatch<CustomerProfileData>(
        onboardingEndpoints.patchCustomer(customerData.id),
        customerData,
      );

      localStorage.setItem(
        "customerData",
        JSON.stringify(newCustomerData.data),
      );
      localStorage.setItem("submitDate", customerData.submitDate);
    }

    // UPDATE customer preferences
    for (const company of companies) {
      if (company.name == "National Debtline" && company.raw?.shareNow == 'Y') {
        if (!company.ndlData?.isPreferencesLoaded) {
          const payload = {
            customerId: customerData.customerId,
            preferredTimeForNdl: hasMobileNumber ? company.ndlData?.preferredTimeForNdl : null,
            debtAdviceContactPreferenceNdl: hasMobileNumber ? 'N' : 'Y',
            voicemailConsentNdl: company.ndlData?.voicemailConsentNdl
              ? "Y"
              : "N",
            emailMeNdl: company.ndlData?.emailMeNdl ? "Y" : "N",
            textMeNdl: hasMobileNumber ? "Y" : "N",
            companyName: company.raw?.lenderName,
            sharingId: company.raw?.id,
            accountNumber: company.raw?.accountNumber,
          };

          await httpPost<ApiResponse>(
            customerEndpoints.customerPrefrences(),
            payload,
          );
        } else {
          const payload = {
            ...company.ndlData?.customerPrefrences,
            customerId: customerData.customerId,
            preferredTimeForNdl: hasMobileNumber ? company.ndlData?.preferredTimeForNdl : null,
            debtAdviceContactPreferenceNdl: hasMobileNumber ? 'N' : 'Y',
            voicemailConsentNdl: company.ndlData?.voicemailConsentNdl
              ? "Y"
              : "N",
            emailMeNdl: company.ndlData?.emailMeNdl ? "Y" : "N",
            textMeNdl: hasMobileNumber ? "Y" : "N",
          };

          await httpPut<ApiResponse>(
            customerEndpoints.updateCustomerPrefrences(
              String(company.ndlData?.customerPrefrences?.id),
            ),
            payload,
          );
        }
      }
    }

    navigate("/share-success");
  }

  const renderCompanyCard = (company: Company) => {
    if (company.raw?.lastSharedDate && company.raw?.isSharing != "Y") {
      return;
    } else if (company.raw?.isDeleted == "Y") {
      // if deleted condition
      return;
    }

    const isExpanded = expandedCompanies.includes(company.id);

    return (
      <Paper
        key={company.id}
        elevation={0}
        sx={{
          p: isMobile ? 2 : 3,
          mb: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: "#fff",
          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            mb: 1,
          }}
        >
          <Avatar
            sx={{
              bgcolor: "#DBEAFE",
              color: theme.palette.primary.contrastText,
              width: isMobile ? 45 : 52,
              height: isMobile ? 45 : 52,
              borderRadius: 2,
            }}
          >
            <AccountBalanceIcon
              sx={{ fontSize: isMobile ? 22 : 26, color: "#2562EB" }}
            />
          </Avatar>

          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box>
                <Typography
                  variant={isMobile ? "body1" : "h5"}
                  sx={{ fontWeight: 400, color: "#0B3D3A", mb: 0 }}
                >
                  {company.name}{" "}
                  {false && company.raw?.source == "customer_profile" && (
                    <strong>(Your Referrer)</strong>
                  )}
                </Typography>
                <Typography
                  data-private
                  variant="h6"
                  sx={{ color: "#ADB5BD", fontWeight: 400 }}
                >
                  {renderCompanyReference(company.reference)}
                </Typography>
              </Box>
              {((company.raw?.lastSharedDate || company.raw?.submitDate) &&
                company.disableShare &&
                company.raw?.isSharing == "Y") ||
              (company.raw?.submitDate &&
                company.disableShare &&
                company.raw?.isSharing == "Y" &&
                company.isFp) ? (
                <IconButton
                  size="small"
                  onClick={() => toggleDeleteCompanyDialog(company)}
                >
                  <DeleteIcon fontSize="small" sx={{ color: "#FF0000" }} />
                </IconButton>
              ) : company.raw?.lastSharedDate && company.raw?.submitDate ? (
                <IconButton
                  size="small"
                  onClick={() => toggleDeleteCompanyDialog(company)}
                >
                  <DeleteIcon fontSize="small" sx={{ color: "#FF0000" }} />
                </IconButton>
              ) : (!company.agentSubmitted ?
                <Box display="flex" gap={1} alignItems="center">
                  <IconButton
                    size="small"
                    onClick={() => toggleEditCompanyDialog(company)}
                  >
                    <EditIcon fontSize="small" sx={{ color: "#ADB5BD" }} />
                  </IconButton>
                </Box> : null
              )}
            </Box>
          </Box>
        </Box>

        {company.address && (
          <Typography
            variant="h6"
            sx={{ color: theme.palette.text.secondary, mb: 2, fontWeight: 400 }}
          >
            Address: {company.address}
          </Typography>
        )}

        {/* {company.paymentOfferData && (
          <Typography
            variant="h6"
            sx={{ color: theme.palette.text.secondary, mb: 2, fontWeight: 400 }}
          >
            Repayment offer: £
            {company.paymentOfferData?.paymentOfferAmount || 0} • Payment day:{" "}
            {company.paymentOfferData?.paymentPreferredFrequency}
          </Typography>
        )} */}

        <Box>
          <Box
            onClick={() => !company.disableShare && toggleCompanyShare(company)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 0.5,
              backgroundColor: company.disableShare
                ? "#9CA3AE"
                : company.raw?.shareNow == "Y"
                  ? "#2562EB"
                  : "#EFF6FF",
              border: `1px solid ${company.disableShare ? ThemeColours.borderGrey : "#BFDBFE"}`,
              borderRadius: 1,
              mb: isExpanded ? 2 : 0,
              cursor: company.disableShare ? "default" : "pointer",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                color: company.disableShare
                  ? ThemeColours.borderGrey
                  : company.raw?.shareNow == "Y"
                    ? "#fff"
                    : "#1E40AF",
              }}
            >
              Add To Share
            </Typography>

            <CheckboxMui
              checked={company.raw?.shareNow === "Y"}
              onChange={() => toggleCompanyShare(company)}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              size="medium"
              disabled={company.disableShare}
              sx={{
                "&.Mui-checked": {
                  color: "#fff",
                },
              }}
            />
          </Box>

          {company.disableShare && (
            <Box
              sx={{
                borderLeft: 4,
                borderColor: "#FF0F17",
                pl: 1,
                ml: 0.3,
                mb: 1.3,
                mt: 2,
                color: "#505A67",
              }}
            >
              <Typography variant="body1">
                {company.notSupported ? `We are no longer able to send post to this company, if
                          you wish to share your budget you can download and
                          send via e-mail.` : `The same budget can only be shared once per day. Please try
                again tomorrow, or edit your budget to share again.`}{" "}
              </Typography>
            </Box>
          )}

          {/* Cadent Gas */}
          {company.name == "Cadent Gas" && company.raw?.shareNow == "Y" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  pt: 1.5,
                  mt: 1.5,
                  pb: 1.5,
                  backgroundColor: "#E8EFEE",
                  border: "1px solid #165F54",
                  borderRadius: 1,
                  mb: 0,
                }}
              >
                <Stack>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    Got gas appliances at home? Click below to list them and get
                    the right support.
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                    sx={{
                      borderRadius: 1,
                      textTransform: "none",
                      fontWeight: 500,
                      py: 1,
                      boxShadow: "none",
                      maxWidth: 310,
                      backgroundColor: "#165F54",
                      fontSize: 16,
                    }}
                    onClick={() => {
                      setOpenCadentGasDialog(true);
                      setDeclarationCompany(company);
                    }}
                  >
                    View or edit my gas appliances
                  </Button>

                  {company.applianceCount > 0 && (
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 300,
                        mb: 0,
                        mt: 1,
                        color: ThemeColours.labelVar2,
                      }}
                    >
                      {company.applianceCount} Appliance added
                    </Typography>
                  )}
                </Stack>
              </Box>
            </>
          )}

          {/* YWCT Declaration Not Completed */}
          {company.name == "Yorkshire Water Community Trust" &&
            company.raw?.shareNow === "Y" &&
            (company.declarationStatus == "not-completed" ||
              company.declarationStatus == "rejected") && (
              <>
                <Box
                  sx={{
                    borderLeft: 4,
                    borderColor: "#165F54",
                    pl: 1,
                    ml: 0.3,
                    mb: 1.3,
                    mt: 2,
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body1">
                    Need to Accept Consent to Share With Yorkshire Water
                    Community Trust
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    pt: 1.5,
                    pb: 2,
                    backgroundColor: "#E8EFEE",
                    border: "1px solid #165F54",
                    borderRadius: 1,
                    mb: 0,
                  }}
                >
                  <Stack>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                      To Share With Yorkshire Water Community Trust please{" "}
                      <u>Accept the consent</u>
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 400, mb: 1 }}>
                      - Accept Required Declarations<br></br>- Upload Required
                      Documents
                      <br></br>- Accept the Consent<br></br>
                    </Typography>

                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      endIcon={
                        <ArrowForwardIcon sx={{ ml: 2, fontSize: 22 }} />
                      }
                      sx={{
                        borderRadius: 1,
                        textTransform: "none",
                        fontWeight: 500,
                        py: 1.3,
                        boxShadow: "none",
                        width: 220,
                        backgroundColor: "#165F54",
                        fontSize: 16,
                      }}
                      onClick={() => {
                        setOpenYWCTDeclarationDialog(true);
                        setDeclarationCompany(company);
                      }}
                    >
                      Open Declaration
                    </Button>
                  </Stack>
                </Box>
              </>
            )}

          {/* YWCT Declaration Accepted */}
          {company.name == "Yorkshire Water Community Trust" &&
            company.raw?.shareNow === "Y" &&
            company.declarationStatus == "accepted" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  pt: 1.5,
                  pb: 2,
                  backgroundColor: "#E8EFEE",
                  border: "1px solid #165F54",
                  borderRadius: 1,
                  mb: 0,
                  mt: 1.5,
                }}
              >
                <Stack>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    You are all set to share your information with Yorkshire
                    Water Community Trust
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 400, mb: 0.5 }}>
                    <Link
                      sx={{ color: "#000", cursor: "pointer" }}
                      onClick={() => {
                        setOpenYWCTDeclarationDialog(true);
                        setDeclarationCompany(company);
                      }}
                    >
                      Click here
                    </Link>
                    ‎ if you wish to agree to the declaration and share your
                    information with Yorkshire Water Community Trust
                  </Typography>
                </Stack>
              </Box>
            )}

          {/* YWCT Declaration Rejected */}
          {company.name == "Yorkshire Water Community Trust" &&
            company.declarationStatus == "rejected" &&
            company.raw?.shareNow === "N" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  pt: 1.5,
                  pb: 2,
                  backgroundColor: "#E8EFEE",
                  border: "1px solid #165F54",
                  borderRadius: 1,
                  mb: 0,
                  mt: 1.5,
                }}
              >
                <Stack>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    You have rejected the declaration, therefore, your
                    information will not be shared with Yorkshire Water
                    Community Trust
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 400, mb: 0.5 }}>
                    <Link
                      sx={{ color: "#000", cursor: "pointer" }}
                      onClick={() => {
                        setOpenYWCTDeclarationDialog(true);
                        setDeclarationCompany(company);
                      }}
                    >
                      Click here
                    </Link>
                    ‎ if you wish to agree to the declaration and share your
                    information with Yorkshire Water Community Trust
                  </Typography>
                </Stack>
              </Box>
            )}

          {/* NDL Component */}
          {company.name === "National Debtline" &&
            company.raw?.shareNow === "Y" && (
              <>
                {hasMobileNumber ? (
                  <Box mt={1}>
                    <Typography
                      gutterBottom
                      sx={{ color: "#6C757D", fontWeight: 400 }}
                    >
                      Select your preferred time for National Debtline to{" "}
                      <strong>call you for debt advice</strong>
                      <InfoIcon
                        sx={{
                          color: "#165F54",
                          fontSize: 14,
                          ml: 0.7,
                          cursor: "pointer",
                          position: "relative",
                          bottom: 1,
                        }}
                        onClick={() => {
                          toggleInfoDialog({ ...company, name: "NDL-INFO" });
                        }}
                      />
                    </Typography>
                    <FormControl component="fieldset">
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {["Morning", "Afternoon", "Evening"].map((opt) => {
                          const isSelected =
                            company.ndlData?.preferredTimeForNdl === opt;

                          return (
                            <FormControlLabel
                              key={opt}
                              label={
                                opt === "Morning"
                                  ? "Morning (9–12)"
                                  : opt === "Afternoon"
                                    ? "Afternoon (12–3)"
                                    : "Evening (3–7)"
                              }
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => {
                                    const companyId = company.raw?.id;
                                    if (
                                      companyId !== undefined &&
                                      companyId !== null
                                    ) {
                                      setPreferredTimeForNdl(
                                        companyId,
                                        isSelected ? "" : opt, // unselect if already checked
                                      );
                                    }
                                  }}
                                />
                              }
                            />
                          );
                        })}
                      </div>
                    </FormControl>

                    {/* <Typography variant="h6" gutterBottom>
                      I consent to National Debtline leaving me a voicemail if
                      I’m unable to answer their call
                    </Typography>
                    <DoubleToggle
                      value={company.ndlData.voicemailConsentNdl}
                      onChange={(value: boolean) =>
                        setVoicemailConsentNdl(company.raw?.id, value)
                      }
                      selectedColor="#165F54"
                      nonSelectedColor="#d5d5d5"
                      textColor="#FFFFFF"
                    /> */}
                  </Box>
                ) : (
                  <Box mt={1}>
                    <Typography
                      gutterBottom
                      sx={{ color: "#6C757D", fontWeight: 400 }}
                    >
                      Would you like National Debtline to{" "}
                      <strong>email you for debt advice?</strong>
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={company.ndlData?.emailMeNdl}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            const companyId = company.raw?.id;
                            if (companyId !== undefined && companyId !== null) {
                              setEmailMeNdl(String(companyId), e);
                            }
                          }}
                        />
                      }
                      label="Email Me"
                    />
                  </Box>
                )}

                {/* <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>
                  Choose how you’d like to receive your next steps:{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>

                <FormControl component="fieldset">
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={company.ndlData?.emailMeNdl}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const companyId = company.raw?.id;
                            if (companyId !== undefined && companyId !== null) {
                              setEmailMeNdl(String(companyId), e.target.checked);
                            }
                          }}
                        />
                      }
                      label="Email Me"
                    />
                    {hasMobileNumber && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={company.ndlData?.textMeNdl}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setTextMeNdl(company.raw?.id, e);
                            }}
                          />
                        }
                        label="Text Me"
                      />
                    )}
                  </FormGroup>
                </FormControl> */}
              </>
            )}

          {/* UU Declaration Not Completed */}
          {company.name == "United Utilities" &&
            company.raw?.shareNow === "Y" &&
            (company.declarationStatus == "not-completed" ||
              company.declarationStatus == "rejected") && (
              <>
                <Box
                  sx={{
                    borderLeft: 4,
                    borderColor: "#165F54",
                    pl: 1,
                    ml: 0.3,
                    mb: 1.3,
                    mt: 2,
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body1">
                    Need to Accept Consent to Share With United Utilities
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    pt: 1.5,
                    pb: 2,
                    backgroundColor: "#E8EFEE",
                    border: "1px solid #165F54",
                    borderRadius: 1,
                    mb: 0,
                  }}
                >
                  <Stack>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                      To Share With United Utilities please{" "}
                      <u>Accept the consent</u>
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 400, mb: 1 }}>
                      - Accept Required Declarations<br></br>- Upload Required
                      Documents
                      <br></br>- Accept the Consent<br></br>
                    </Typography>

                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      endIcon={
                        <ArrowForwardIcon sx={{ ml: 2, fontSize: 22 }} />
                      }
                      sx={{
                        borderRadius: 1,
                        textTransform: "none",
                        fontWeight: 500,
                        py: 1.3,
                        boxShadow: "none",
                        width: 220,
                        backgroundColor: "#165F54",
                        fontSize: 16,
                      }}
                      onClick={() => {
                        setOpenUUDeclarationDialog(true);
                        setDeclarationCompany(company);
                      }}
                    >
                      Open Declaration
                    </Button>
                  </Stack>
                </Box>
              </>
            )}

          {/* UU Declaration Accepted */}
          {company.name == "United Utilities" &&
            company.raw?.shareNow === "Y" &&
            company.declarationStatus == "accepted" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  pt: 1.5,
                  pb: 2,
                  backgroundColor: "#E8EFEE",
                  border: "1px solid #165F54",
                  borderRadius: 1,
                  mb: 0,
                  mt: 1.5,
                }}
              >
                <Stack>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    You are all set to share your information with United
                    Utilities
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 400, mb: 0.5 }}>
                    <Link
                      sx={{ color: "#000", cursor: "pointer" }}
                      onClick={() => {
                        setOpenUUDeclarationDialog(true);
                        setDeclarationCompany(company);
                      }}
                    >
                      Click here
                    </Link>
                    ‎ if you wish to agree to the declaration and share your
                    information with United Utilities
                  </Typography>
                </Stack>
              </Box>
            )}

          {/* UU Declaration Rejected */}
          {company.name == "United Utilities" &&
            company.declarationStatus == "rejected" &&
            company.raw?.shareNow === "N" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  pt: 1.5,
                  pb: 2,
                  backgroundColor: "#E8EFEE",
                  border: "1px solid #165F54",
                  borderRadius: 1,
                  mb: 0,
                  mt: 1.5,
                }}
              >
                <Stack>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    You have rejected the declaration, therefore, your
                    information will not be shared with United Utilities
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 400, mb: 0.5 }}>
                    <Link
                      sx={{ color: "#000", cursor: "pointer" }}
                      onClick={() => {
                        setOpenUUDeclarationDialog(true);
                        setDeclarationCompany(company);
                      }}
                    >
                      Click here
                    </Link>
                    ‎ if you wish to agree to the declaration and share your
                    information with United Utilities
                  </Typography>
                </Stack>
              </Box>
            )}

          {company.name == "CDER Group Limited" &&
            company.raw?.shareNow === "Y" && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    pt: 1.5,
                    pb: 2,
                    mt: 1.5,
                    backgroundColor: "#E8EFEE",
                    border: "1px solid #165F54",
                    borderRadius: 1,
                    mb: 0,
                  }}
                >
                  <Stack>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                      Confirmation
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 400, mb: 1 }}>
                      Please upload evidence of your income and any
                      vulnerabilities. Without this information, CDER Group are
                      unable to consider any arrangement on your account.
                    </Typography>

                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                      sx={{
                        borderRadius: 1,
                        textTransform: "none",
                        fontWeight: 500,
                        py: 1,
                        boxShadow: "none",
                        maxWidth: 190,
                        backgroundColor: "#165F54",
                        fontSize: 16,
                      }}
                      onClick={() => {
                        navigate("/documents?ref=cder");
                      }}
                    >
                      Add Document
                    </Button>
                  </Stack>
                </Box>
              </>
            )}

          {company.name != "Cadent Gas" &&
            company.raw?.shareNow === "Y" &&
            company.documents.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Select Documents To Share
                  <InfoIcon
                    sx={{
                      color: "#165F54",
                      fontSize: 14,
                      ml: 0.7,
                      cursor: "pointer",
                      position: "relative",
                      bottom: 1,
                    }}
                    onClick={() => {
                      toggleInfoDialog(company);
                    }}
                  />
                </Typography>

                <Stack spacing={2}>
                  {company.documents.map((doc) => (
                    <Box
                      key={doc.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.5,
                        backgroundColor: "#fff",
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#DCFCE7",
                          borderRadius: 2,
                        }}
                      >
                        <DescriptionOutlinedIcon sx={{ color: "#165F54" }} />
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {doc.name}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {doc.size} • Uploaded {doc.uploadedDate}
                        </Typography>
                      </Box>

                      <Checkbox
                        checked={company.selectedDocuments.includes(doc.id)}
                        onChange={() =>
                          handleDocumentToggle(company.id, doc.id)
                        }
                        size="medium"
                        disabled={company.raw?.shareNow !== "Y"}
                      />
                    </Box>
                  ))}
                </Stack>
                {company.raw?.shareNow !== "Y" && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 1,
                      display: "block",
                    }}
                  >
                    Turn on “Add To Share” to choose which documents to include.
                  </Typography>
                )}
              </Box>
            )}

          {company.raw?.submitDate && !company.agentSubmitted && ( 
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <AccessTimeFilledIcon sx={{ fontSize: 16, color: "#6B727F" }} />
              <Typography variant="body2" sx={{ color: "#6B727F" }}>
                Last shared on{" "}
                {company.raw?.submitDate
                  ? formatDate(company.raw?.submitDate)
                  : "-"}
              </Typography>
            </Box>
          )}

          {false && company.documents.length === 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: theme.palette.warning.light + "20",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: theme.palette.warning.dark }}
              >
                No documents available to share with this company.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    );
  };

  const renderCompanySection = (
    title: string,
    companies: Company[],
    ref?: React.RefObject<HTMLDivElement>,
    id?: string,
  ) => {
    if (companies.length === 0) return null;

    return (
      <Box
        ref={ref || null}
        id={id || undefined}
        sx={{ mb: 4, scrollMarginTop: "100px" }}
      >
        {companies.map(renderCompanyCard)}
      </Box>
    );
  };

  const renderDeclarationModal = () => (
    <Dialog
      open={declarationOpen}
      onClose={handleDeclarationClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Transition as any}
      sx={{
        "& .MuiDialog-container": {
          alignItems: { xs: "stretch", md: "flex-end" },
        },
        "& .MuiPaper-root": {
          m: 0,
          width: "100%",
          maxWidth: { md: 600 },
          borderTopLeftRadius: { xs: 0, md: 16 },
          borderTopRightRadius: { xs: 0, md: 16 },
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Upload Utilities Declaration
        </Typography>
        <IconButton onClick={handleDeclarationClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Confirming Household Status */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Confirming household status
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              I can confirm this is a Single Adult household and provide you
              with the support you need.
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Chip
                label="Yes"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    status: "yes",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.status === "yes"
                      ? theme.palette.success.main
                      : "transparent",
                  color:
                    householdDeclaration.status === "yes"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.status === "yes" ? theme.palette.success.main : theme.palette.divider}`,
                  fontWeight: 500,
                }}
              />
              <Chip
                label="No"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({ ...prev, status: "no" }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.status === "no"
                      ? theme.palette.error.main
                      : "transparent",
                  color:
                    householdDeclaration.status === "no"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.status === "no" ? theme.palette.error.main : theme.palette.divider}`,
                  fontWeight: 500,
                }}
              />
            </Box>
          </Box>

          {/* Confirming Household Circumstances */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Confirming household circumstances
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              I can confirm there are no changes to my household and I can
              provide you with the support you need.
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Chip
                label="Yes"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    circumstances: "yes",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.circumstances === "yes"
                      ? theme.palette.success.main
                      : "transparent",
                  color:
                    householdDeclaration.circumstances === "yes"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.circumstances === "yes" ? theme.palette.success.main : theme.palette.divider}`,
                  fontWeight: 500,
                }}
              />
              <Chip
                label="No"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    circumstances: "no",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.circumstances === "no"
                      ? theme.palette.error.main
                      : "transparent",
                  color:
                    householdDeclaration.circumstances === "no"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.circumstances === "no" ? theme.palette.error.main : theme.palette.divider}`,
                  fontWeight: 500,
                }}
              />
            </Box>
          </Box>

          {/* Previous Credit */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Previous Credit
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              Is there a change in the household in charge of utility payments
              or any previous credit with this company?
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Chip
                label="Yes"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    previousCredit: "yes",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.previousCredit === "yes"
                      ? theme.palette.success.main
                      : "transparent",
                  color:
                    householdDeclaration.previousCredit === "yes"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.previousCredit === "yes" ? theme.palette.success.main : theme.palette.divider}`,
                  fontWeight: 500,
                }}
              />
              <Chip
                label="No"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    previousCredit: "no",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.previousCredit === "no"
                      ? theme.palette.error.main
                      : "transparent",
                  color:
                    householdDeclaration.previousCredit === "no"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.previousCredit === "no" ? theme.palette.error.main : theme.palette.divider}`,
                  fontWeight: 500,
                }}
              />
            </Box>
          </Box>

          {/* Household Financial Position */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Household Financial Position
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              Do you donate to the property either in neutral circumstances or
              by rental property?
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label="Yes"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    financialPositionChange: "yes",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.financialPositionChange === "yes"
                      ? theme.palette.success.main
                      : "transparent",
                  color:
                    householdDeclaration.financialPositionChange === "yes"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.financialPositionChange === "yes" ? theme.palette.success.main : theme.palette.divider}`,
                  fontWeight: 500,
                  mb: 1,
                }}
              />
              <Chip
                label="No"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    financialPositionChange: "no",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.financialPositionChange === "no"
                      ? theme.palette.error.main
                      : "transparent",
                  color:
                    householdDeclaration.financialPositionChange === "no"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.financialPositionChange === "no" ? theme.palette.error.main : theme.palette.divider}`,
                  fontWeight: 500,
                  mb: 1,
                }}
              />
              <Chip
                label="Not Applicable"
                onClick={() =>
                  setHouseholdDeclaration((prev) => ({
                    ...prev,
                    financialPositionChange: "not-applicable",
                  }))
                }
                sx={{
                  backgroundColor:
                    householdDeclaration.financialPositionChange ===
                    "not-applicable"
                      ? theme.palette.info.main
                      : "transparent",
                  color:
                    householdDeclaration.financialPositionChange ===
                    "not-applicable"
                      ? "#fff"
                      : theme.palette.text.primary,
                  border: `1px solid ${householdDeclaration.financialPositionChange === "not-applicable" ? theme.palette.info.main : theme.palette.divider}`,
                  fontWeight: 500,
                  mb: 1,
                }}
              />
            </Stack>
          </Box>

          <Divider />

          {/* File Upload Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Drag & drop files here
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                mb: 2,
                display: "block",
              }}
            >
              or tap to browse your device
            </Typography>

            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => {
                const element = document.getElementById("file-upload-input");
                if (element) {
                  (element as HTMLInputElement).click();
                }
              }}
              sx={{
                border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragging
                  ? theme.palette.action.hover
                  : theme.palette.background.default,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CloudUploadIcon
                sx={{
                  fontSize: 48,
                  color: theme.palette.text.secondary,
                  mb: 1,
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Drop files here or click to browse
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.disabled }}
              >
                Supported formats: PDF, JPG, PNG (Max 10MB)
              </Typography>
            </Box>

            <input
              id="file-upload-input"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFileUpload(e.target.files)
              }
            />
          </Box>

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Document List Required
              </Typography>
              <Stack spacing={1}>
                {uploadedFiles.map((file) => (
                  <Box
                    key={file.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 1.5,
                      backgroundColor: theme.palette.success.light + "20",
                      border: `1px solid ${theme.palette.success.light}`,
                      borderRadius: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: theme.palette.success.light + "40",
                        borderRadius: 1,
                      }}
                    >
                      <DescriptionIcon
                        sx={{ color: theme.palette.success.main }}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {file.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {formatFileSize(file.size)} • Uploaded{" "}
                        {file.uploadDate.toLocaleDateString()}
                      </Typography>
                    </Box>

                    <CheckCircleIcon
                      sx={{ color: theme.palette.success.main, fontSize: 20 }}
                    />

                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(file.id)}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Warning Message */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              p: 2,
              backgroundColor: theme.palette.error.light + "20",
              borderRadius: 1,
              border: `1px solid ${theme.palette.error.light}`,
            }}
          >
            <WarningIcon
              sx={{ color: theme.palette.error.main, fontSize: 20, mt: 0.5 }}
            />
            <Typography
              variant="caption"
              sx={{ color: theme.palette.error.dark, lineHeight: 1.5 }}
            >
              You will not share this same E with companies that you're not
              already shared it with in the past 24 hours
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 1.5,
            backgroundColor: theme.palette.primary.main,
            color: "#fff",
            borderRadius: 2,
            cursor: "pointer",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
          onClick={handleDeclarationSubmit}
        >
          Save & continue
        </Box>
      </DialogActions>
    </Dialog>
  );

  const renderAddCompanyDialog = () => {
    return (
      <Dialog
        open={addCompanyOpen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              maxHeight: "90vh",
              padding: "0px 0px 20px 0px",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <AddCompanyDialog
          onClose={() => {
            setAddCompanyOpen(false);
          }}
          balanceAmount={summary.balanceAmount}
          ieMstrLenderData={ieMstrLenderData}
          digitalLenders={digitalLenders}
          setCompanies={
            setCompanies as React.Dispatch<React.SetStateAction<any[]>>
          }
          setCompanyShareLastUpdateDate={setCompanyShareLastUpdateDate}
          freepostCreditorNamesData={freepostCreditorNamesData}
          declarationData={declarationData}
          companies={companies as any[]}
          source="share-page"
        />
      </Dialog>
    );
  };

  const renderShareHistoryDialog = () => {
    return (
      <Dialog
        open={shareHistoryOpen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              height: "100vh",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <ShareHistoryDialog
          onClose={() => {
            setShareHistoryOpen(false);
          }}
          shareBudgetsLogic={shareBudgetsLogic as unknown as () => boolean}
          companies={companies}
          setCompanies={setCompanies}
        />
      </Dialog>
    );
  };

  const renderUUDeclarationDialog = () => {
    return (
      <Dialog
        open={openUUDeclarationDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              height: "100vh",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <UUDeclarationDialog
          onClose={() => {
            setOpenUUDeclarationDialog(false);
          }}
          handleDocumentToggle={handleDocumentToggle}
          company={declarationCompany}
          setCompanies={setCompanies}
        />
      </Dialog>
    );
  };

  const renderYWCTDeclarationDialog = () => {
    return (
      <Dialog
        open={openYWCTDeclarationDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              height: "100vh",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <YWCTDeclarationDialog
          onClose={() => {
            setOpenYWCTDeclarationDialog(false);
          }}
          company={declarationCompany}
          setCompanies={setCompanies}
          toggleCompanyShare={toggleCompanyShare}
        />
      </Dialog>
    );
  };

  const renderCadentGasDialog = () => {
    return (
      <Dialog
        open={openCadentGasDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              height: "100vh",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <CadentGasDialog
          onClose={() => {
            setOpenCadentGasDialog(false);
          }}
          company={declarationCompany}
          setCompanies={setCompanies}
        />
      </Dialog>
    );
  };

  const renderEditCompanyDialog = () => {
    return (
      <EditCompanyDialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
        }}
        setCompanies={setCompanies}
        company={selectedEditCompany}
        setOpenDeleteDialog={setOpenDeleteDialog}
        companies={companies}
      />
    );
  };

  const renderInfoDialog = () => {
    return (
      <InfoDialog
        open={openInfoDialog}
        setOpenUUDeclarationDialog={setOpenUUDeclarationDialog}
        onClose={() => {
          setOpenInfoDialog(false);
        }}
        company={selectedEditCompany}
      />
    );
  };

  const renderDeleteCompanyDialog = () => {
    return (
      <DeleteCompanyDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
        }}
        setCompanies={setCompanies}
        company={selectedEditCompany}
      />
    );
  };

  const renderPlaceholder = (isMobile?: boolean | undefined) => {
    if (!hasCompaniesLoaded) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80%",
            marginTop: isMobile ? -3 : 10,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height={isMobile ? "auto" : "100%"}
        textAlign="center"
        marginTop={5}
        marginBottom={5}
      >
        <img
          className="placeholder-illustration"
          alt="Placeholder Illustration"
          src={placeholderIllustration}
          style={{ marginBottom: 16, height: 150 }}
        />

        <Typography
          sx={{
            color: "#013B3E",
            fontWeight: 700,
            fontSize: 24,
            lineHeight: 1,
          }}
        >
          You haven’t added any <br></br> companies yet.
        </Typography>
      </Box>
    );
  };

  // ==================== Main Render ====================

  return (
    <DashboardLayout>
      {/* Mobile View Content */}
      <Box sx={{ display: { xs: "block", md: "none" }, pb: 14 }}>
        {renderHeader()}
        {renderTabs()}

        {hasNoCompanies && hasCompaniesLoaded && renderPlaceholder(true)}
        <Box sx={{ mb: 4, scrollMarginTop: "100px" }}>
          {renderCompanySection(
            "All",
            groupedCompanies.all || [],
            allSectionRef,
            "all-section",
          )}
        </Box>
        {/* Sticky Bottom Navigation - Mobile */}
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: "calc(89px + env(safe-area-inset-bottom))",
            left: 0,
            right: 0,
            zIndex: 999,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "white",
            px: 2,
            py: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "stretch",
            }}
          >
            <Box sx={{ textAlign: "left" }}>
              <Typography
                component="span"
                sx={{
                  color: "#013B3E",
                  fontWeight: 500,
                  fontSize: 16,
                  lineHeight: 1.2,
                }}
              >
                Sharing With :{" "}
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    lineHeight: 1.2,
                    color: "#013B3E",
                  }}
                >
                  {totalCompaniesCount}{" "}
                  {totalCompaniesCount === 1 ? "Company" : "Companies"}
                </Typography>
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              startIcon={<ShareIcon />}
              sx={{
                borderRadius: 1,
                textTransform: "none",
                fontWeight: 600,
                py: 1.5,
                boxShadow: "none",
              }}
              onClick={() => shareData()}
              disabled={isSharing}
            >
              {isSharing ? "Sharing..." : "Share Securely"}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Desktop View Content */}
      <Box sx={{ display: { xs: "none", md: "block" }, pb: 14 }}>
        {renderHeader()}
        {renderTabs()}

        {hasNoCompanies && renderPlaceholder()}

        <Box sx={{ mb: 4, scrollMarginTop: "100px" }}>
          {renderCompanySection(
            "All",
            groupedCompanies.all || [],
            allSectionRef,
            "all-section",
          )}
        </Box>

        {/* Sticky Bottom Navigation - Desktop */}
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "white",
            px: 2,
            py: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              margin: "0 auto",
              px: 4,
              maxWidth: "1216px",
            }}
          >
            <Typography
              component="span"
              sx={{
                fontWeight: 500,
                fontSize: 16,
                color: "#013B3E",
              }}
            >
              Sharing With :{" "}
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: "#013B3E",
                }}
              >
                {totalCompaniesCount}{" "}
                {totalCompaniesCount === 1 ? "Company" : "Companies"}
              </Typography>
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<ShareIcon />}
              sx={{
                borderRadius: 1,
                textTransform: "none",
                fontWeight: 600,
                py: 1.3,
                px: 3.5,
                boxShadow: "none",
              }}
              onClick={() => shareData()}
              disabled={isSharing}
            >
              {isSharing ? "Sharing..." : "Share Securely"}
            </Button>
          </Box>
        </Paper>
      </Box>

      {renderDeclarationModal()}
      {renderAddCompanyDialog()}
      {renderEditCompanyDialog()}
      {renderInfoDialog()}
      {renderDeleteCompanyDialog()}
      {renderUUDeclarationDialog()}
      {renderYWCTDeclarationDialog()}
      {renderCadentGasDialog()}
      {renderShareHistoryDialog()}

      <Dialog
        open={isAdditionalSupportOpen}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              height: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              height: "100vh",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <AdditionalSupportModal
          onClose={() => {
            setIsAdditionalSupportOpen(false);
          }}
          freepostCreditorNamesList={freepostCreditorNamesData}
          setFreePostLastUpdateDate={setFreePostLastUpdateDate}
          digitalData={digitalData}
          ieMstrLenderData={ieMstrLenderData}
          digitalLenders={digitalLenders}
          summary={summary}
          setCompanies={setCompanies}
          companies={companies}
          setCompanyShareLastUpdateDate={setCompanyShareLastUpdateDate}
        />
      </Dialog>
    </DashboardLayout>
  );
};

export default SharePage;
